import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Flex } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import ObjectForm, { OBJECT_FORM_MODE } from '@components/forms/ObjectForm';
import ProjectPickerModal from '@components/modal/projectPickerModal/ProjectPickerModal';
import { useConfirm } from '@contexts/Confirm.context';
import { useToast } from '@contexts/Toast.context';
import { createTaxonomyNode, updateTaxonomyNode } from '@utils/api';
import { DEFAULT_PROJECT_VIEW_MODE } from '@utils/constants';
import { buildProjectUrl } from '@utils/projectPath';

import ObjectDetailsEmpty from './objectDetails/ObjectDetailsEmpty';
import ObjectSummary from './objectDetails/ObjectSummary';
import TrainingDetailCard from './objectDetails/TrainingDetailCard';
import {
	buildObjectKey,
	buildObjectPath,
	formatCount,
	getTrainingGate,
} from './objectDetails/objectsUtils';
import { useSelectedObject } from './objectDetails/useSelectedObject';

/**
 * Right-hand column of the Manage objects page.
 *
 * Shows the taxonomy node the tree selected (summary + training gate) and, once
 * "Train Object" is pressed on a node that has enough annotations, swaps to the
 * create form for a child of that node. Creating posts to the taxonomy and
 * lets the tree refetch; the new node only appears once its model is trained.
 *
 * The tree card's edit button opens the same form pre-filled for the selected
 * node (`editingUuid`, owned by the page so the card can set it); saving
 * patches the node in place. As in the design, the edit form keeps the Label
 * row and the annotation count, both taken from the node's parent — the
 * examples a re-train would learn from.
 */
const ObjectDetails = ({ selectedUuid = null, mode, editingUuid = null, onEditDone }) => {
	const { selectedObject, objects } = useSelectedObject(selectedUuid, mode);
	// The uuid the create form was opened from; the form shows only while that
	// node is the selection.
	const [creatingFrom, setCreatingFrom] = useState(null);
	const [isPickingProject, setIsPickingProject] = useState(false);

	// Moving to another node abandons the draft and closes the picker, so coming
	// back to the node shows its details rather than the form again. Done as a
	// state reset during render (React's "adjust state on prop change" pattern)
	// rather than an effect, so no frame renders the stale form.
	const currentUuid = selectedObject?.uuid ?? null;
	const [shownUuid, setShownUuid] = useState(currentUuid);
	if (currentUuid !== shownUuid) {
		setShownUuid(currentUuid);
		setCreatingFrom(null);
		setIsPickingProject(false);
	}
	const navigate = useNavigate();
	const { confirm } = useConfirm();
	const { addToast } = useToast();
	const queryClient = useQueryClient();

	const createObject = useMutation({
		mutationFn: createTaxonomyNode,
		onSuccess: (_, payload) => {
			// The tree on this page and the picker's per-project taxonomy.
			queryClient.invalidateQueries({ queryKey: ['objectTaxonomy'] });
			queryClient.invalidateQueries({ queryKey: ['taxonomyNodes'] });
			setCreatingFrom(null);
			addToast({
				id: 'object-create',
				bg: 'success',
				title: 'Training and creation started',
				message: `We will notify you when “${payload.name}” is ready to use.`,
			});
		},
		onError: () => {
			addToast({
				id: 'object-create-error',
				bg: 'danger',
				title: 'Could not create the object',
				message: 'Please try again.',
			});
		},
	});

	const updateObject = useMutation({
		mutationFn: ({ uuid, ...payload }) => updateTaxonomyNode(uuid, payload),
		onSuccess: (_, payload) => {
			queryClient.invalidateQueries({ queryKey: ['objectTaxonomy'] });
			queryClient.invalidateQueries({ queryKey: ['taxonomyNodes'] });
			onEditDone?.();
			addToast({
				id: 'object-update',
				bg: 'success',
				title: `Object “${payload.name}” updated`,
			});
		},
		onError: () => {
			addToast({
				id: 'object-update-error',
				bg: 'danger',
				title: 'Could not save the changes',
				message: 'Please try again.',
			});
		},
	});

	if (!selectedObject) return <ObjectDetailsEmpty />;

	const gate = getTrainingGate(selectedObject);
	const isCreating = creatingFrom === selectedObject.uuid;
	const isEditing = editingUuid === selectedObject.uuid;
	const parentObject = objects.find(object => object.uuid === selectedObject.parent_uuid);

	// Open the project in collection mode with the object to annotate in the
	// query string, for the map page to pick up.
	const handleProjectSelected = project => {
		setIsPickingProject(false);
		navigate(
			`${buildProjectUrl(project.uuid, DEFAULT_PROJECT_VIEW_MODE)}?annotate=${encodeURIComponent(selectedObject.key)}`
		);
	};

	const handleSubmit = async values => {
		const confirmed = await confirm({
			title: 'Create and train',
			message: `“${values.name}” will be created under “${selectedObject.name}” and its model trained from ${formatCount(gate.annotationCount)} annotated examples. Continue?`,
		});
		if (!confirmed) return;

		// Body of POST /taxonomy/nodes.
		createObject.mutate({
			name: values.name,
			parent_uuid: selectedObject.uuid,
			key: buildObjectKey(values.name),
			color: values.color,
			description: values.description,
			trained_label: values.trained_label || null,
			// The form's `size` (metres) is the API's `object_size`; left out when empty.
			...(values.size != null ? { object_size: values.size } : {}),
			// Already uploaded by the form; omitted so the node inherits the parent's.
			...(values.thumbnail ? { thumbnail: values.thumbnail } : {}),
		});
	};

	// The edit endpoint does not take `object_size` yet: the form only shows the stored
	// value, and a change to it is not sent.
	const handleEditSubmit = values => {
		updateObject.mutate({
			uuid: selectedObject.uuid,
			name: values.name,
			description: values.description,
			color: values.color,
			trained_label: values.trained_label || null,
			// The key of the existing image, or of a newly uploaded one; omitted
			// when the user removed it so the node falls back to its parent's.
			...(values.thumbnail ? { thumbnail: values.thumbnail } : {}),
		});
	};

	// The form derives its title, fields and label choices from the node itself;
	// the container only says which mode, and what to do with the result.
	const formMode = isEditing
		? {
				key: `edit-${selectedObject.uuid}`,
				mode: OBJECT_FORM_MODE.EDIT,
				object: selectedObject,
				parent: parentObject,
				loading: updateObject.isPending,
				onSubmit: handleEditSubmit,
				onCancel: onEditDone,
			}
		: isCreating
			? {
					key: `create-${selectedObject.uuid}`,
					mode: OBJECT_FORM_MODE.CREATE,
					object: selectedObject,
					loading: createObject.isPending,
					onSubmit: handleSubmit,
					onCancel: () => setCreatingFrom(null),
				}
			: null;

	// antd applies initialValues on mount only, so the key remounts the form per
	// node and per mode: a second edit or create starts from its own values.
	if (formMode) return <ObjectForm {...formMode} />;

	return (
		<Flex vertical gap={12}>
			<ObjectSummary
				object={selectedObject}
				path={buildObjectPath(selectedObject, objects)}
			/>
			<TrainingDetailCard
				object={selectedObject}
				gate={gate}
				onTrain={() => setCreatingFrom(selectedObject.uuid)}
				onSelectProject={() => setIsPickingProject(true)}
			/>
			<ProjectPickerModal
				open={isPickingProject}
				subtitle={`Annotation happens on a project’s imagery. Pick where to annotate “${selectedObject.name}” — existing annotations there will be shown.`}
				mode={mode}
				actionLabel="Annotate"
				onSelect={handleProjectSelected}
				onCancel={() => setIsPickingProject(false)}
			/>
		</Flex>
	);
};

export default ObjectDetails;
